import * as React from 'react';
import * as _ from 'lodash';
import { Dispatch, connect } from 'react-redux';
import {
    IApplicationState, AppActionCreators, IAppState, IMicrositeResponse, MicrositeActionCreators, IMicrositeLookupDataResponse,
    IMicrositeRecipientSurveyResponse, ISaveMicrositeRecipientSurveyResponse
} from '@nkr/store/index';
import { IListItem, IMicrositeRecipientSurveyRequest } from '@nkr/models/index';
import { Alert, Button, Checkbox, Col, ControlLabel, FormControl, FormGroup, HelpBlock, Panel, Radio, Row } from 'react-bootstrap';
import { AddEditWrapper, FormWrapper } from '../styles';
import { toast } from 'react-toastify';
import MaskedInput from 'react-maskedinput';

interface IOption {
    id: number,
    description: string
}

interface ICheckOption {
    name: string,
    label: string
}

const dialysisTypeList: IOption[] = [
    { id: 1, description: 'Peritoneal' },
    { id: 2, description: 'Home Hemodialysis' },
    { id: 3, description: 'In-Center Hemodialysis' },
    { id: 4, description: 'Other' }
];

// 0=Unknown 11=Below 12 12-25=value 26=Above 25
const egfrList: IOption[] = [
    { id: 11, description: 'Below 12' },
    ..._.range(12, 26).map(x => ({ id: x, description: `${x}` })),
    { id: 26, description: 'Above 25' },
    { id: 0, description: 'Unknown' }
];

const communityOrgOptions: ICheckOption[] = [
    { name: 'communityOrgYmca', label: 'YMCA / YWCA' },
    { name: 'communityOrgReligious', label: 'Church / Religious Organization' },
    { name: 'communityOrgPta', label: 'PTA' }
];

const socialMediaOptions: ICheckOption[] = [
    { name: 'socialFacebook', label: 'Facebook' },
    { name: 'socialInstagram', label: 'Instagram' },
    { name: 'socialTikTok', label: 'TikTok' },
    { name: 'socialLinkedIn', label: 'LinkedIn' },
    { name: 'socialNone', label: 'None' }
];

const communicationOptions: ICheckOption[] = [
    { name: 'commPhoneCall', label: 'Phone Call' },
    { name: 'commText', label: 'Text' },
    { name: 'commEmail', label: 'Email' },
    { name: 'commNoPreference', label: 'No Preference' }
];

const contactTimeList: IOption[] = [
    { id: 1, description: '9a-12p' },
    { id: 2, description: '12p-3p' },
    { id: 3, description: '3p-6p' }
];

// exclusive option -> the rest of its group
const exclusiveGroups: { [name: string]: string[] } = {
    socialNone: ['socialFacebook', 'socialInstagram', 'socialTikTok', 'socialLinkedIn', 'socialOther'],
    commNoPreference: ['commPhoneCall', 'commText', 'commEmail']
};

const checkboxFields = [
    ...communityOrgOptions.map(x => x.name), 'communityOrgOther',
    ...socialMediaOptions.map(x => x.name), 'socialOther',
    ...communicationOptions.map(x => x.name)
];

interface IValidation {
    dialysisStartDate: boolean,
    communityOrgOtherDetails: boolean,
    socialOtherDetails: boolean
}

const initialValidation: IValidation = {
    dialysisStartDate: true,
    communityOrgOtherDetails: true,
    socialOtherDetails: true
};

interface IProps {
    micrositeId: number;
    centerId: number;
    isAdmin: boolean;
    previousUrl: string;
    previousName: string;
}

interface IStateProps {
    app: IAppState;
}

interface IDispatchProps {
    showLoading: (message: string) => Promise<void>,
    hideLoading: () => void,
    getMicrosite: (id: number, forCoach: boolean, confirm: boolean) => Promise<IMicrositeResponse>,
    getLookupData: (centerId: number, micrositeRecipientId: number) => Promise<IMicrositeLookupDataResponse>,
    getRecipientSurvey: (micrositeRecipientId: number) => Promise<IMicrositeRecipientSurveyResponse>,
    saveRecipientSurvey: (request: IMicrositeRecipientSurveyRequest) => Promise<ISaveMicrositeRecipientSurveyResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    firstName: string,
    lastName: string,
    birthdate: string,
    genderId: number,
    isOnDialysis: boolean | null,
    dialysisType: number,
    dialysisStartDate: string,
    egfr: number,
    communityOrgReligious: boolean,
    communityOrgYmca: boolean,
    communityOrgPta: boolean,
    communityOrgOther: boolean,
    communityOrgOtherDetails: string,
    socialFacebook: boolean,
    socialInstagram: boolean,
    socialTikTok: boolean,
    socialLinkedIn: boolean,
    socialNone: boolean,
    socialOther: boolean,
    socialOtherDetails: string,
    commPhoneCall: boolean,
    commText: boolean,
    commEmail: boolean,
    commNoPreference: boolean,
    employmentStatus: number,
    isMilitaryVeteran: boolean | null,
    branchOfService: number,
    preferredContactTime: number,
    validation: IValidation,
    genderList: IListItem[],
    armedServiceList: IListItem[],
    employmentStatusList: IListItem[],
    dirty: boolean,
    disableSubmit: boolean
}

class EditDemographics extends React.Component<TProps, IState> {
    readonly state: IState = {
        firstName: '',
        lastName: '',
        birthdate: '',
        genderId: 0,
        isOnDialysis: null,
        dialysisType: -1,
        dialysisStartDate: '',
        egfr: -1,
        communityOrgReligious: false,
        communityOrgYmca: false,
        communityOrgPta: false,
        communityOrgOther: false,
        communityOrgOtherDetails: '',
        socialFacebook: false,
        socialInstagram: false,
        socialTikTok: false,
        socialLinkedIn: false,
        socialNone: false,
        socialOther: false,
        socialOtherDetails: '',
        commPhoneCall: false,
        commText: false,
        commEmail: false,
        commNoPreference: false,
        employmentStatus: -1,
        isMilitaryVeteran: null,
        branchOfService: -1,
        preferredContactTime: -1,
        validation: { ...initialValidation },
        genderList: [],
        armedServiceList: [],
        employmentStatusList: [],
        dirty: false,
        disableSubmit: false
    };

    componentDidMount = async () => {
        await this.props.showLoading('Getting the demographics, one moment please...');

        try {
            let [micrositeResponse, lookupDataResponse, surveyResponse] = await Promise.all([
                this.props.getMicrosite(this.props.micrositeId, false, false),
                this.props.getLookupData(this.props.centerId, this.props.micrositeId),
                this.props.getRecipientSurvey(this.props.micrositeId)
            ]);

            if (micrositeResponse.data.success && lookupDataResponse.data.success && surveyResponse.data.success) {
                let { firstName, lastName, birthdate, genderId } = micrositeResponse.data.results;
                let { genderList, armedServiceList, employmentStatusList } = lookupDataResponse.data.results;
                let { survey } = surveyResponse.data.results;

                let state = _.cloneDeep(this.state);

                Object.assign(state, {
                    firstName,
                    lastName,
                    birthdate,
                    genderId,
                    genderList,
                    armedServiceList,
                    employmentStatusList
                });

                if (survey != null) {
                    Object.assign(state, survey, {
                        dialysisType: survey.dialysisType ?? -1,
                        dialysisStartDate: survey.dialysisStartDate ?? '',
                        egfr: survey.egfr ?? -1,
                        communityOrgOtherDetails: survey.communityOrgOtherDetails ?? '',
                        socialOtherDetails: survey.socialOtherDetails ?? '',
                        employmentStatus: survey.employmentStatus ?? -1,
                        branchOfService: survey.branchOfService ?? -1,
                        preferredContactTime: survey.preferredContactTime ?? -1
                    });
                }

                this.setState(state);
            }
        } finally {
            this.props.hideLoading();
        }
    };

    getPreviousUrl = () => {
        return this.props.isAdmin ?
            `/admin/microsite/${this.props.previousUrl}`
            :
            `/center/${this.props.centerId}/microsite/${this.props.previousUrl}`;
    };

    validate = (state: IState): IValidation => {
        let { isOnDialysis, dialysisStartDate, communityOrgOther, communityOrgOtherDetails, socialOther, socialOtherDetails } = state;

        return {
            dialysisStartDate: isOnDialysis !== true || dialysisStartDate.replace(/[_/]/g, '') == '' || this.isValidStartDate(dialysisStartDate),
            communityOrgOtherDetails: !communityOrgOther || communityOrgOtherDetails.trim().length <= 255,
            socialOtherDetails: !socialOther || socialOtherDetails.trim().length <= 255
        };
    }

    isValid = () => {
        let { dialysisStartDate, communityOrgOtherDetails, socialOtherDetails } = this.state.validation;

        return dialysisStartDate && communityOrgOtherDetails && socialOtherDetails;
    }

    isValidStartDate = (value: string): boolean => {
        let match = /^(\d{2})\/(\d{4})$/.exec(value);

        if (match == null) return false;

        let month = parseInt(match[1], 10);
        let year = parseInt(match[2], 10);

        if (month < 1 || month > 12 || year < 1900) return false;

        let today = new Date();

        return year < today.getFullYear() || (year == today.getFullYear() && month <= today.getMonth() + 1);
    }

    determineAge = (birthdateValue: string): number | null => {
        const dob = new Date(birthdateValue);

        if (isNaN(dob.getTime())) return null;

        const today = new Date();
        let age = today.getFullYear() - dob.getFullYear();
        const m = today.getMonth() - dob.getMonth();

        if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
            age--;
        }

        return age;
    };

    save = async () => {
        let s = this.state;

        const onDialysis = s.isOnDialysis === true;
        const notOnDialysis = s.isOnDialysis === false;
        const toNullable = (value: number) => value > -1 ? value : null;

        let request: IMicrositeRecipientSurveyRequest = {
            micrositeRecipientId: this.props.micrositeId,
            isOnDialysis: s.isOnDialysis,
            dialysisType: onDialysis ? toNullable(s.dialysisType) : null,
            dialysisStartDate: onDialysis && s.dialysisStartDate.replace(/[_/]/g, '') != '' ? s.dialysisStartDate : null,
            egfr: notOnDialysis ? toNullable(s.egfr) : null,
            communityOrgReligious: s.communityOrgReligious,
            communityOrgYmca: s.communityOrgYmca,
            communityOrgPta: s.communityOrgPta,
            communityOrgOther: s.communityOrgOther,
            communityOrgOtherDetails: s.communityOrgOther ? s.communityOrgOtherDetails.trim() : '',
            socialFacebook: s.socialFacebook,
            socialInstagram: s.socialInstagram,
            socialTikTok: s.socialTikTok,
            socialLinkedIn: s.socialLinkedIn,
            socialNone: s.socialNone,
            socialOther: s.socialOther,
            socialOtherDetails: s.socialOther ? s.socialOtherDetails.trim() : '',
            commPhoneCall: s.commPhoneCall,
            commText: s.commText,
            commEmail: s.commEmail,
            commNoPreference: s.commNoPreference,
            employmentStatus: toNullable(s.employmentStatus),
            isMilitaryVeteran: s.isMilitaryVeteran,
            branchOfService: s.isMilitaryVeteran === true ? toNullable(s.branchOfService) : null,
            preferredContactTime: s.commPhoneCall === true ? toNullable(s.preferredContactTime) : null
        };

        this.setState({ disableSubmit: true });

        await this.props.showLoading('Saving demographics, one moment please...');

        try {
            let response = await this.props.saveRecipientSurvey(request);

            if (response.data.success) {
                toast.success('Demographics saved successfully', {
                    onClose: () => {
                        window.location.href = this.getPreviousUrl();
                    },
                });
            } else {
                this.setState({ disableSubmit: false });
            }
        } catch (error) {
            this.setState({ disableSubmit: false });
        } finally {
            this.props.hideLoading();
        }
    };

    handleFieldChange = (e: React.ChangeEvent<FormControl & HTMLInputElement & HTMLSelectElement>) => {
        let state = _.cloneDeep(this.state);
        let { name, value } = e.currentTarget;

        let textFields = [
            'dialysisStartDate',
            'communityOrgOtherDetails',
            'socialOtherDetails'
        ];

        if (textFields.includes(name)) {
            state[name] = value;
        }

        let dropdownFields = [
            'dialysisType',
            'egfr',
            'employmentStatus',
            'branchOfService',
            'preferredContactTime'
        ];

        if (dropdownFields.includes(name)) {
            state[name] = value !== '' ? parseInt(value, 10) : -1;
        }

        if (checkboxFields.includes(name)) {
            let checked = e.currentTarget.checked;

            state[name] = checked;

            if (checked) {
                if (exclusiveGroups[name] != null) {
                    exclusiveGroups[name].forEach(x => state[x] = false);
                } else {
                    let exclusive = Object.keys(exclusiveGroups).find(x => exclusiveGroups[x].includes(name));

                    if (exclusive != null) {
                        state[exclusive] = false;
                    }
                }
            }

            if (!state.commPhoneCall) {
                state.preferredContactTime = -1;
            }
        }

        if (name == 'isOnDialysis') {
            state.isOnDialysis = value === 'true';

            if (state.isOnDialysis) {
                state.egfr = -1;
            } else {
                state.dialysisType = -1;
                state.dialysisStartDate = '';
            }
        }

        if (name == 'isMilitaryVeteran') {
            state.isMilitaryVeteran = value === 'true';

            if (!state.isMilitaryVeteran) {
                state.branchOfService = -1;
            }
        }

        if (state.dirty) {
            state.validation = this.validate(state);
        }

        this.setState(state);
    };

    handleSave = async () => {
        let validation = this.validate(this.state);

        this.setState({
            validation,
            dirty: true
        }, async () => {
            if (!this.isValid()) {
                window.scrollTo(0, 0);
            } else {
                await this.save();
            }
        });
    };

    handleCancel = () => {
        window.location.href = this.getPreviousUrl();
    };

    renderCheckboxes = (options: ICheckOption[]) => {
        return options.map(item => (
            <Checkbox key={item.name} name={item.name} checked={this.state[item.name] == true} onChange={this.handleFieldChange} style={{ marginTop: 0 }}>
                {item.label}
            </Checkbox>
        ));
    };

    renderOther = (checkName: string, detailsName: string, valid: boolean) => {
        return (
            <>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox name={checkName} checked={this.state[checkName] == true} onChange={this.handleFieldChange} style={{ margin: '0 10px 0 0' }}>
                        Other
                    </Checkbox>
                    <FormControl
                        name={detailsName}
                        maxLength={255}
                        disabled={this.state[checkName] != true}
                        value={this.state[detailsName]}
                        onChange={this.handleFieldChange}
                    />
                </div>

                {this.state.dirty && !valid && (
                    <HelpBlock>
                        Other must be 255 characters or less
                    </HelpBlock>
                )}
            </>
        );
    };

    render = () => {
        const { dirty, validation } = this.state;

        const age = this.determineAge(this.state.birthdate);
        const gender = this.state.genderList.find(x => x.id == this.state.genderId);

        return (
            <AddEditWrapper>
                {dirty && !this.isValid() && (
                    <Row>
                        <Col xs={12} md={9} mdOffset={1}>
                            <Alert bsStyle='danger'>
                                <div className='validation-section'>Please correct the items in red...</div>
                            </Alert>
                        </Col>
                    </Row>
                )}

                <Row>
                    <Col xs={12} md={9} mdOffset={1}>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginTop: 10,
                            }}
                        >
                            <h1 className='text-left' style={{ margin: 0 }}>
                                Edit Demographics{this.state.firstName ? ` - ${this.state.firstName} ${this.state.lastName}` : ''}
                            </h1>
                            <div className='dropdown pull-right'>
                                <Button
                                    bsStyle='link'
                                    onClick={this.handleCancel}
                                >
                                    Return to {this.props.previousName}
                                </Button>
                            </div>
                        </div>

                        <br />

                        <Panel>
                            <Panel.Body>
                                <FormWrapper horizontal>
                                    <FormGroup>
                                        <Col xs={12} sm={4} componentClass={ControlLabel}>Dialysis:</Col>
                                        <Col xs={12} sm={8}>
                                            <Radio inline name='isOnDialysis' value='true' checked={this.state.isOnDialysis === true} onChange={this.handleFieldChange}>Yes</Radio>
                                            <Radio inline name='isOnDialysis' value='false' checked={this.state.isOnDialysis === false} onChange={this.handleFieldChange}>No</Radio>
                                        </Col>
                                    </FormGroup>

                                    {this.state.isOnDialysis === true && (
                                        <>
                                            <FormGroup>
                                                <Col xs={12} sm={4} componentClass={ControlLabel} htmlFor='dialysisType'>Form of Dialysis:</Col>
                                                <Col xs={12} sm={4}>
                                                    <FormControl
                                                        componentClass='select'
                                                        name='dialysisType'
                                                        value={this.state.dialysisType}
                                                        onChange={this.handleFieldChange}
                                                    >
                                                        <option value={-1}>- Please Select -</option>
                                                        {dialysisTypeList.map((item, index) => (
                                                            <option key={'dt-' + index} value={item.id}>{item.description}</option>
                                                        ))}
                                                    </FormControl>
                                                </Col>
                                            </FormGroup>

                                            <FormGroup validationState={dirty && !validation.dialysisStartDate ? 'error' : null}>
                                                <Col xs={12} sm={4} componentClass={ControlLabel} htmlFor='dialysisStartDate'>Dialysis Start Date:</Col>
                                                <Col xs={12} sm={2}>
                                                    <MaskedInput
                                                        name='dialysisStartDate'
                                                        className='form-control'
                                                        mask='11/1111'
                                                        placeholder='mm/yyyy'
                                                        value={this.state.dialysisStartDate}
                                                        onChange={this.handleFieldChange}
                                                    />
                                                </Col>
                                                <Col xs={12} sm={6}>
                                                    <HelpBlock style={{ color: '#c9372e' }}>
                                                        {dirty && !validation.dialysisStartDate ? 'Enter a valid month and year that is not in the future' : 'This can be an estimated start date'}
                                                    </HelpBlock>
                                                </Col>
                                            </FormGroup>
                                        </>
                                    )}

                                    {this.state.isOnDialysis === false && (
                                        <FormGroup>
                                            <Col xs={12} sm={4} componentClass={ControlLabel} htmlFor='egfr'>Current eGFR:</Col>
                                            <Col xs={12} sm={2}>
                                                <FormControl
                                                    componentClass='select'
                                                    name='egfr'
                                                    value={this.state.egfr}
                                                    onChange={this.handleFieldChange}
                                                >
                                                    <option value={-1}>- Please Select -</option>
                                                    {egfrList.map((item, index) => (
                                                        <option key={'egfr-' + index} value={item.id}>{item.description}</option>
                                                    ))}
                                                </FormControl>
                                            </Col>
                                        </FormGroup>
                                    )}

                                    <FormGroup validationState={dirty && !validation.communityOrgOtherDetails ? 'error' : null}>
                                        <Col xs={12} sm={4} componentClass={ControlLabel}>Community Involvement:</Col>
                                        <Col xs={12} sm={6}>
                                            {this.renderCheckboxes(communityOrgOptions)}
                                            {this.renderOther('communityOrgOther', 'communityOrgOtherDetails', validation.communityOrgOtherDetails)}
                                        </Col>
                                    </FormGroup>

                                    <FormGroup validationState={dirty && !validation.socialOtherDetails ? 'error' : null}>
                                        <Col xs={12} sm={4} componentClass={ControlLabel}>Social Media Platforms:</Col>
                                        <Col xs={12} sm={6}>
                                            {this.renderCheckboxes(socialMediaOptions)}
                                            {this.renderOther('socialOther', 'socialOtherDetails', validation.socialOtherDetails)}
                                        </Col>
                                    </FormGroup>

                                    <FormGroup>
                                        <Col xs={12} sm={4} componentClass={ControlLabel}>Preferred Contact Method:</Col>
                                        <Col xs={12} sm={6}>
                                            {this.renderCheckboxes(communicationOptions)}
                                        </Col>
                                    </FormGroup>

                                    {this.state.commPhoneCall && (
                                        <FormGroup>
                                            <Col xs={12} sm={4} componentClass={ControlLabel} htmlFor='preferredContactTime'>Preferred Contact Time (Mon-Fri):</Col>
                                            <Col xs={12} sm={2}>
                                                <FormControl
                                                    componentClass='select'
                                                    name='preferredContactTime'
                                                    value={this.state.preferredContactTime}
                                                    onChange={this.handleFieldChange}
                                                >
                                                    <option value={-1}>- Please Select -</option>
                                                    {contactTimeList.map((item, index) => (
                                                        <option key={'ct-' + index} value={item.id}>{item.description}</option>
                                                    ))}
                                                </FormControl>
                                            </Col>
                                        </FormGroup>
                                    )}

                                    <FormGroup>
                                        <Col xs={12} sm={4} componentClass={ControlLabel} htmlFor='employmentStatus'>Employment Status:</Col>
                                        <Col xs={12} sm={4}>
                                            <FormControl
                                                componentClass='select'
                                                name='employmentStatus'
                                                value={this.state.employmentStatus}
                                                onChange={this.handleFieldChange}
                                            >
                                                <option value={-1}>- Please Select -</option>
                                                {this.state.employmentStatusList.map((item, index) => (
                                                    <option key={'emp-' + index} value={item.id}>{item.description}</option>
                                                ))}
                                            </FormControl>
                                        </Col>
                                    </FormGroup>

                                    <FormGroup>
                                        <Col xs={12} sm={4} componentClass={ControlLabel}>Active Duty or Military Veteran:</Col>
                                        <Col xs={12} sm={2}>
                                            <Radio inline name='isMilitaryVeteran' value='true' checked={this.state.isMilitaryVeteran === true} onChange={this.handleFieldChange}>Yes</Radio>
                                            <Radio inline name='isMilitaryVeteran' value='false' checked={this.state.isMilitaryVeteran === false} onChange={this.handleFieldChange}>No</Radio>
                                        </Col>

                                        {this.state.isMilitaryVeteran === true && (
                                            <>
                                                <Col xs={12} sm={1} componentClass={ControlLabel} htmlFor='branchOfService'>Branch:</Col>
                                                <Col xs={12} sm={3}>
                                                    <FormControl
                                                        componentClass='select'
                                                        name='branchOfService'
                                                        value={this.state.branchOfService}
                                                        onChange={this.handleFieldChange}
                                                    >
                                                        <option value={-1}>- Please Select -</option>
                                                        {this.state.armedServiceList.map((item, index) => (
                                                            <option key={'asl-' + index} value={item.id}>{item.description}</option>
                                                        ))}
                                                    </FormControl>
                                                </Col>
                                            </>
                                        )}
                                    </FormGroup>

                                    <FormGroup>
                                        <Col xs={12} sm={4} componentClass={ControlLabel}>Gender:</Col>
                                        <Col xs={12} sm={8}>
                                            <FormControl.Static>{gender != null ? gender.description : '—'}</FormControl.Static>
                                        </Col>
                                    </FormGroup>

                                    <FormGroup>
                                        <Col xs={12} sm={4} componentClass={ControlLabel}>Date of Birth:</Col>
                                        <Col xs={12} sm={2}>
                                            <FormControl.Static>{this.state.birthdate || '—'}</FormControl.Static>
                                        </Col>

                                        <Col xs={12} sm={1} componentClass={ControlLabel}>Age:</Col>
                                        <Col xs={12} sm={2}>
                                            <FormControl.Static>{age != null ? age : '—'}</FormControl.Static>
                                        </Col>
                                    </FormGroup>
                                </FormWrapper>
                            </Panel.Body>
                        </Panel>

                        <FormGroup style={{ marginTop: 20 }}>
                            <Col xs={12} style={{ display: 'flex', justifyContent: 'center', }}>
                                <Button
                                    bsStyle='primary'
                                    bsSize='lg'
                                    className='col-xs-2'
                                    disabled={this.state.disableSubmit}
                                    style={{ marginRight: 5 }}
                                    onClick={this.handleSave}
                                >
                                    Save
                                </Button>
                                <Button
                                    className='col-xs-2'
                                    onClick={this.handleCancel}
                                >
                                    Cancel
                                </Button>
                            </Col>
                        </FormGroup>
                    </Col>
                </Row>
            </AddEditWrapper>
        );
    };
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        app: state.app,
    };
};

const mapDispatchToProps = (dispatch: Dispatch<any>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            return dispatch(AppActionCreators.hideLoading());
        },
        getMicrosite: (id, forCoach, confirm) => {
            return dispatch(MicrositeActionCreators.getMicrosite(id, forCoach, confirm));
        },
        getLookupData: (centerId, micrositeRecipientId) => {
            return dispatch(MicrositeActionCreators.getLookupData(centerId, micrositeRecipientId));
        },
        getRecipientSurvey: (micrositeRecipientId) => {
            return dispatch(MicrositeActionCreators.getRecipientSurvey(micrositeRecipientId));
        },
        saveRecipientSurvey: (request) => {
            return dispatch(MicrositeActionCreators.saveRecipientSurvey(request));
        },
    };
};

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps
)(EditDemographics);